import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { TrendingUp, Zap, Sprout, DollarSign, Building2, HeartPulse, Leaf, Users } from 'lucide-react';

const WhatWeDo = () => {
  const impactMetrics = [
    { label: 'Solar Deployed', value: 300, unit: 'MW', progress: 75, icon: <Zap /> },
    { label: 'Veteran Agri-Plots', value: 12750, unit: '', progress: 85, icon: <Sprout /> },
    { label: 'Capital Deployed', value: 50, unit: 'M USD', progress: 60, icon: <DollarSign /> },
    { label: 'Carbon Reduction', value: 25, unit: '%', progress: 70, icon: <Leaf /> }
  ];

  const divisions = [
    {
      name: 'Energy',
      icon: <Zap className="w-8 h-8" />,
      focus: 'Renewable Power Solutions',
      initiative: 'Defence Solar Project',
      slug: 'defence-solar',
      description: '1GW Solar Initiative powering Africa\'s sustainable future',
      color: '#00CED1',
      image: 'https://images.unsplash.com/photo-1496065187959-7f07b8353c55?q=85&w=800'
    },
    {
      name: 'Commodities',
      icon: <Sprout className="w-8 h-8" />,
      focus: 'Ethical Trade & Agriculture',
      initiative: 'Blockchain Traceability',
      slug: 'blockchain-traceability',
      description: 'Climate-smart agriculture and transparent mineral value chains',
      color: '#FF8C00',
      image: 'https://images.unsplash.com/photo-1644325349124-d1756b79dd42?q=85&w=800'
    },
    {
      name: 'VC/PE',
      icon: <TrendingUp className="w-8 h-8" />,
      focus: 'Innovation Capital',
      initiative: 'Visionaries Rise Accelerator',
      slug: 'visionaries-rise',
      description: 'Funding Africa\'s next generation of sustainable startups',
      color: '#00CED1',
      image: 'https://images.unsplash.com/photo-1644088379091-d574269d422f?q=85&w=800'
    },
    {
      name: 'Strategic Advisory',
      icon: <Building2 className="w-8 h-8" />,
      focus: 'Consulting & Policy',
      initiative: 'EV Network Planning',
      slug: 'ev-network',
      description: 'Guiding governments and corporations toward ESG excellence',
      color: '#FF8C00',
      image: 'https://images.unsplash.com/photo-1568952433726-3896e3881c65?q=85&w=800'
    },
    {
      name: 'Health',
      icon: <HeartPulse className="w-8 h-8" />,
      focus: 'Wellness Solutions',
      initiative: 'Telemedicine Hubs',
      slug: 'telemedicine-hubs',
      description: 'Expanding healthcare access through technology',
      color: '#00CED1',
      image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?q=85&w=800'
    },
    {
      name: 'Sustainability',
      icon: <Leaf className="w-8 h-8" />,
      focus: 'ESG Integration',
      initiative: 'Carbon Credit Brokerage',
      slug: 'carbon-credits',
      description: 'Embedding environmental responsibility across all operations',
      color: '#FF8C00',
      image: 'https://images.unsplash.com/photo-1523961131990-5ea7c61b2107?q=85&w=800'
    }
  ];

  const timeline = [
    {
      year: '2020',
      era: 'Founding',
      event: 'XSI established during COVID-19',
      type: 'past'
    },
    {
      year: '2023',
      era: '$20M Fund Launch',
      event: 'Major capital raising milestone',
      type: 'past'
    },
    {
      year: '2025',
      era: '1GW Solar Rollout',
      event: 'Massive renewable energy deployment',
      type: 'present'
    },
    {
      year: '2027',
      era: 'Pan-African EV Network',
      event: 'Continental electric mobility infrastructure',
      type: 'future'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1758519291531-e96279895745?crop=entropy&cs=srgb&fm=jpg&q=85')`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#00CED1]/90 via-[#00CED1]/70 to-transparent"></div>
          <div className="absolute inset-0 opacity-20" style={{
            backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(255,255,255,0.1) 2px, rgba(255,255,255,0.1) 4px)',
          }}></div>
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-6 text-white text-center">
          <h1 className="text-6xl font-bold mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            What We Do
          </h1>
          <p className="text-2xl text-gray-200">Transforming Vision into Measurable Impact</p>
        </div>
      </section>

      {/* Operations Dashboard */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Impact Dashboard
            </h2>
            <div className="w-24 h-1 bg-[#00CED1] mx-auto mb-8"></div>
            <p className="text-xl text-gray-600">Real-time metrics of our continental transformation</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {impactMetrics.map((metric, index) => (
              <Card key={index} className="border-2 border-gray-200 hover:border-[#00CED1] transition-all duration-300 hover:shadow-xl">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 bg-[#00CED1]/10 rounded-full text-[#00CED1]">
                      {metric.icon}
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-gray-900">
                        {metric.value}
                        <span className="text-xl text-[#FF8C00] ml-1">{metric.unit}</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm font-semibold text-gray-600 mb-3">{metric.label}</p>
                  <Progress value={metric.progress} className="h-2" />
                  <p className="text-xs text-gray-500 mt-2 text-right">{metric.progress}% of target</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* ESG Scorecard */}
          <div className="mt-12 grid md:grid-cols-2 gap-8">
            <Card className="bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-500 overflow-hidden">
              <div className="relative h-40 overflow-hidden">
                <img src="https://images.unsplash.com/photo-1466611653911-95081537e5b7?q=85&w=800" alt="Renewable energy environment" className="w-full h-full object-cover" />
              </div>
              <CardContent className="p-8 text-center">
                <h3 className="text-2xl font-bold text-green-800 mb-4">Environmental</h3>
                <div className="text-6xl font-bold text-green-600 mb-2">25%↓</div>
                <p className="text-green-700 font-semibold">Carbon Emissions Reduction</p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-500 overflow-hidden">
              <div className="relative h-40 overflow-hidden">
                <img src="https://images.unsplash.com/photo-1531482615713-2afd69097998?q=85&w=800" alt="Community empowerment" className="w-full h-full object-cover" />
              </div>
              <CardContent className="p-8 text-center">
                <h3 className="text-2xl font-bold text-blue-800 mb-4">Social</h3>
                <div className="text-6xl font-bold text-blue-600 mb-2">78%↑</div>
                <p className="text-blue-700 font-semibold">Livelihoods Improved</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Division Profiles */}
      <section className="py-20 px-6 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Our Divisions
            </h2>
            <div className="w-24 h-1 bg-[#FF8C00] mx-auto mb-8"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {divisions.map((division, index) => (
              <Card 
                key={index} 
                className="hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden"
              >
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={division.image} 
                    alt={division.name}
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="flex items-center gap-3">
                      <div 
                        className="p-3 rounded-full backdrop-blur-sm" 
                        style={{ backgroundColor: `${division.color}40`, color: 'white' }}
                      >
                        {division.icon}
                      </div>
                      <h3 className="text-2xl font-bold text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        {division.name}
                      </h3>
                    </div>
                  </div>
                </div>
                <div 
                  className="h-2" 
                  style={{ backgroundColor: division.color }}
                ></div>
                <CardHeader className="bg-white">
                  <Badge className="bg-[#FF8C00] hover:bg-[#e07d00] text-white">{division.focus}</Badge>
                </CardHeader>
                <CardContent className="bg-white">
                  <p className="text-gray-700 mb-4">{division.description}</p>
                  <div className="pt-4 border-t border-gray-200">
                    <Link
                      to={`/explore/${division.slug}`}
                      className="text-sm font-semibold hover:underline underline-offset-4"
                      style={{ color: division.color }}
                      data-testid={`division-link-${division.slug}`}
                    >
                      Key Initiative: {division.initiative} →
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Strategic Timeline */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Our Journey
            </h2>
            <div className="w-24 h-1 bg-[#00CED1] mx-auto mb-8"></div>
          </div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-gray-300 via-[#00CED1] to-[#FF8C00]"></div>

            <div className="space-y-16">
              {timeline.map((item, index) => (
                <div 
                  key={index} 
                  className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'} gap-8`}
                >
                  <div className={`flex-1 ${index % 2 === 0 ? 'text-right' : 'text-left'}`}>
                    <Card className={`inline-block ${
                      item.type === 'present' 
                        ? 'border-2 border-[#00CED1] shadow-xl' 
                        : 'border border-gray-300'
                    }`}>
                      <CardContent className="p-6">
                        <Link to="/explore/our-story" className="text-4xl font-bold text-[#FF8C00] mb-2 block hover:underline underline-offset-4" data-testid={`timeline-link-${item.year}`}>{item.year}</Link>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{item.era}</h3>
                        <p className="text-gray-600">{item.event}</p>
                        {item.type === 'present' && (
                          <Badge className="mt-3 bg-[#00CED1] hover:bg-[#00b8bb]">In Progress</Badge>
                        )}
                      </CardContent>
                    </Card>
                  </div>

                  <div className="relative z-10">
                    <div className={`w-6 h-6 rounded-full ${
                      item.type === 'present' 
                        ? 'bg-[#00CED1] ring-4 ring-[#00CED1]/30 animate-pulse' 
                        : 'bg-gray-400'
                    }`}></div>
                  </div>

                  <div className="flex-1"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Impact Spotlight */}
      <section className="py-20 px-6 bg-gradient-to-br from-gray-900 to-gray-800 text-white relative overflow-hidden">
        {/* Animated background pattern */}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1575052454050-f553218f2da5?q=85&w=800')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}></div>
        <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-gradient-to-bl from-[#00CED1]/20 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-gradient-to-tr from-[#FF8C00]/20 to-transparent rounded-full blur-3xl"></div>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Impact Spotlight
            </h2>
            <div className="w-24 h-1 bg-[#00CED1] mx-auto mb-8"></div>
            <p className="text-xl text-gray-300">Veteran Empowerment Program</p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="grid grid-cols-2 gap-4">
              <img 
                src="https://images.unsplash.com/photo-1560493676-04071c5f467b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHxzdXN0YWluYWJsZSUyMGFncmljdWx0dXJlfGVufDB8fHx8MTc2NzE1MTU2NXww&ixlib=rb-4.1.0&q=85"
                alt="Sustainable Agriculture"
                className="rounded-lg shadow-2xl col-span-2 h-56 w-full object-cover"
              />
              <img 
                src="https://images.unsplash.com/photo-1625246333195-78d9c38ad449?q=85&w=800"
                alt="Farm irrigation"
                className="rounded-lg shadow-2xl h-40 w-full object-cover"
              />
              <img 
                src="https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?q=85&w=800"
                alt="Solar irrigation power"
                className="rounded-lg shadow-2xl h-40 w-full object-cover"
              />
            </div>
            <div>
              <div className="flex items-center gap-4 mb-6">
                <Users className="w-12 h-12 text-[#00CED1]" />
                <Link to="/explore/veteran-empowerment" className="text-3xl font-bold hover:underline underline-offset-4" data-testid="spotlight-veterans-link">12,750 Veterans Empowered</Link>
              </div>
              <p className="text-lg text-gray-300 mb-6">
                Transforming dry land into thriving solar-irrigated farms, providing sustainable livelihoods 
                for those who served our nation.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-[#00CED1] rounded-full"></div>
                  <p>Solar-powered irrigation systems</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-[#FF8C00] rounded-full"></div>
                  <p>Climate-smart agriculture training</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-[#00CED1] rounded-full"></div>
                  <p>Direct market access for crops</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhatWeDo;